import { ref } from 'vue';
import { systemService } from '../services/systemService.js';

const online = ref(true);
const status = ref('online');

const CHECK_INTERVAL = 5000;
const TIMEOUT_MS = 5000;
const OFFLINE_FAILURE_THRESHOLD = 3;

let consecutiveFailures = 0;
let inFlight = false;
let initialized = false;
let timer = null;

function registerSuccess() {
    consecutiveFailures = 0;
    status.value = 'online';
    online.value = true;
}

function registerFailure() {
    consecutiveFailures += 1;

    if (consecutiveFailures >= OFFLINE_FAILURE_THRESHOLD) {
        status.value = 'offline';
        online.value = false;
        return;
    }

    status.value = 'degraded';
    online.value = true;
}

export async function checkServerStatus() {
    if (inFlight) {
        return false;
    }

    inFlight = true;

    try {
        const data = await systemService.ping({
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            timeoutMs: TIMEOUT_MS,
            retryCount: 0
        });

        if (!data || data.status !== 'ok') {
            registerFailure();
            return false;
        }

        registerSuccess();
        return true;
    } catch {
        registerFailure();
        return false;
    } finally {
        inFlight = false;
    }
}

function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
        void checkServerStatus();
    }
}

export function useServerStatus() {
    return { online, status };
}

export function initServerStatus() {
    if (initialized) {
        return;
    }

    initialized = true;
    void checkServerStatus();
    timer = setInterval(() => void checkServerStatus(), CHECK_INTERVAL);

    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange);
    }
}

export function resetServerStatusForTests() {
    if (timer !== null) {
        clearInterval(timer);
        timer = null;
    }

    if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    }

    consecutiveFailures = 0;
    inFlight = false;
    initialized = false;
    status.value = 'online';
    online.value = true;
}
